import { apiGet, apiPut } from "../_apiShim";
import { UserLog } from "@/types/UserLog";

const BASE_PATH = "/api/v1/user-logs/";

/**
 * Shared with the connection heartbeat (`lib/connection/ConnectionWatcher`),
 * which hits this URL through `api.get` directly so it can carry a
 * per-request timeout the shim's `apiGet` cannot — one path, two callers.
 */
export const NUM_UNREAD_LOGS_PATH = `${BASE_PATH}num-unread`;

/**
 * Get paginated user logs.
 *
 * @param page The page number.
 * @param size The number of logs per page.
 * @returns A promise resolving to a list of user logs or an error response.
 */
export async function getPaginatedUserLogs(
  page: number = 0,
  size: number = 10,
  olderThan: Date,
  read?: boolean
): Promise<UserLog[]> {
  let queryParams = `?page=${encodeURIComponent(
    page
  )}&size=${encodeURIComponent(size)}&olderThan=${encodeURIComponent(
    olderThan.toISOString()
  )}`;

  if (read !== undefined) {
    queryParams += `&read=${read}`;
  }

  const results = await apiGet<UserLog[]>(
    `${BASE_PATH}paginated${queryParams}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );

  if (!results) {
    return [];
  }

  return results;
}

/**
 * Marks specific user logs as read.
 *
 * @param userLogIds Array of user log IDs to be marked as read.
 * @returns A promise resolving to the count of updated logs or an error response.
 */
export async function markUserLogAsRead(
  userLogId: string
): Promise<void> {
  return await apiPut<void>(
    `${BASE_PATH}mark-as-read?userLogId=${userLogId}`,
    {},
    {
      "Content-Type": "application/json",
      "Cache-Control": "no-store",
    },
  );
}

export async function markUserLogsAsRead(): Promise<void> {
  return await apiPut<void>(
    `${BASE_PATH}mark-all-as-read`,
    {},
    {
      "Content-Type": "application/json",
      "Cache-Control": "no-store",
    },
  );
}

/**
 * Checks whether the user has unread logs.
 *
 * @returns A promise resolving to a boolean indicating if there are unread logs.
 */
export async function numUnreadLogs(): Promise<number> {
  return await apiGet<number>(NUM_UNREAD_LOGS_PATH, {
    "Cache-Control": "no-store",
  });
}
